import { MenuItem, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useGetContactsQuery } from '@/api/v1';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { Contact, ContactType } from '@/types/contacts';
import { ModalFlowContext } from '@/types/modalFlowContext';

export const ExistingContactFlowModal: React.FC = (): React.ReactElement => {
  const customer = useCustomer();
  const navigate = useNavigate();
  const modal = useOutletContext<ModalFlowContext>();
  const tr = useTranslator();
  const { data: contactsPage } = useGetContactsQuery(
    { beneficiaryId: customer.beneficiaryId, size: 1000 },
    { skip: !customer.beneficiaryId },
  );
  const contacts = contactsPage?.content;

  const [contactId, setContactId] = React.useState<string>('');

  const onNext = React.useCallback((): void => {
    navigate(`contact/${contactId}`);
  }, [contactId, navigate]);

  const onContactIdChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setContactId(event.target.value);
    },
    [setContactId],
  );

  const getContactName = React.useCallback((contact: Contact): string => {
    return contact.entity.type === ContactType.individual
      ? `${contact.entity.firstName} ${contact.entity.lastName}`
      : contact.entity.legalEntityName;
  }, []);

  const goBack = React.useCallback((): void => {
    modal.close();
  }, [modal]);

  return (
    <ApplicationModal title={tr('SELECT_EXISTING_CONTACT')} onClose={goBack}>
      <Box display="flex" flexDirection="column" gap={6}>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label={tr('CONTACT')}
            select={true}
            value={contactId}
            fullWidth={true}
            onChange={onContactIdChange}>
            {contacts?.map(
              (contact: Contact): React.ReactElement => (
                <MenuItem key={contact.id} value={contact.id}>
                  {getContactName(contact)}
                </MenuItem>
              ),
            ) ?? []}
          </TextField>
        </Box>

        <Button variant="dark" disabled={!contactId} fullWidth={true} onClick={onNext}>
          {tr('NEXT')}
        </Button>
      </Box>
    </ApplicationModal>
  );
};
